import React, { createContext, useState, useMemo, useEffect } from 'react';
import { css } from '@emotion/react';
import { Grid, DialogContent, Tooltip, IconButton } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
    ModalTitle,
    PrimaryButton,
    ResponsiveSidebarDialog,
    StickyModalActions,
    SecondaryButton,
} from '@/styles/blocks';
import { colors } from '@/styles';
import { Link } from 'react-router-dom';

import { Body1, Body2, HR } from './blocks';
import { useExceptionActions, useExceptionState, useExceptionCallbacks, useExceptionInstructions } from './hooks';
import ResolutionRadioGroup from './ResolutionRadioGroup';
import ResolutionSummary from './ResolutionSummary';
import OrderInfo from './OrderInfo';
import ItemInfo from './ItemInfo';
import ExceptionInfo from './ExceptionInfo';
import { useClientUser } from '@/hooks';
import { InfoOutlined } from '@material-ui/icons';
import { captureException } from '@sentry/react';
import { ACTIONS, INSTRUCTIONS } from './constants';

export const ModalContext = createContext();

const DEFAULT_OPTIONS = {
    readOnly: false,
};

const ExceptionResolutionModal = ({ order, exception, loading, items = [], opt = {}, callbacks, route_id }) => {
    const [inFlight, setInflight] = useState(false);
    // A failed resolve used to be swallowed (Sentry only), leaving the admin with a spinner that just
    // stops. Show the message so they know nothing was resolved and can retry.
    const [resolveError, setResolveError] = useState(null);
    useEffect(() => setResolveError(null), [exception?.exception_id]);
    const { user_id, isOnwardAdmin, isCarrierAdmin } = useClientUser();

    const options = useMemo(() => {
        return { ...DEFAULT_OPTIONS, ...opt };
    }, [opt]);

    const [
        {
            instruction,
            isAdmin,
            isCarrier,
            isShipper,
            isValid,
            itemDelivered,
            itemExchanged,
            itemReturned,
            whReturned,
            carrierCleared,
            omitted,
            resolution,
            clearPoNumber,
            tz,
            updates,
        },
        { setAction, setUpdates, setInstruction, setClearPoNumber },
    ] = useExceptionState({
        order,
        exception,
        items,
    });

    const actions = useExceptionActions({ order, omitted, exception, items, isAdmin, isShipper, isCarrier });
    const instructions = useExceptionInstructions({ exception });
    const { resolve, acknowledge } = useExceptionCallbacks({
        order,
        itemDelivered,
        instruction,
        itemExchanged,
        itemReturned,
        whReturned,
        carrierCleared,
        omitted,
        exception,
        items,
        updates,
        resolution,
        route_id,
        callbacks: {
            ...callbacks,
            setInflight,
        },
        user_id,
    });

    if (!exception) {
        return null;
    }

    let resolutions;
    let canResolve = false;
    if (options.readOnly) {
        resolutions = null;
    } else {
        switch (exception.status) {
            case 'RESOLVED': {
                resolutions = (
                    <>
                        <ResolutionSummary exception={exception} route_id={route_id} order={order} />

                        <HR />
                    </>
                );
                break;
            }
            case 'PENDING_CONFIRMATION': {
                resolutions = (
                    <>
                        <ResolutionSummary exception={exception} route_id={route_id} order={order} />

                        {order.oms || isCarrier || isAdmin || isCarrierAdmin ? (
                            <Grid
                                container
                                direction="row"
                                css={css`
                                    margin-top: 20px;
                                `}
                            >
                                <PrimaryButton onClick={acknowledge}>Resolve</PrimaryButton>
                            </Grid>
                        ) : null}

                        <HR />
                    </>
                );
                break;
            }
            case 'OPEN': {
                if (!order.oms && isCarrier && !isAdmin && !isCarrierAdmin) {
                    resolutions = (
                        <>
                            <Grid container direction="row">
                                <Body2
                                    css={css`
                                        color: ${colors.greys[3]};
                                    `}
                                >
                                    Awaiting shipper response
                                </Body2>
                            </Grid>

                            <HR />
                        </>
                    );
                } else {
                    canResolve = true;
                    resolutions = (
                        <>
                            <ResolutionRadioGroup
                                isValid={isValid}
                                route_id={route_id}
                                loading={inFlight || loading}
                                actions={actions}
                                updates={updates}
                                exception={exception}
                                resolution={resolution}
                                instruction={instruction}
                                instructions={instructions}
                                clearPoNumber={clearPoNumber}
                                tz={tz}
                                callbacks={{
                                    resolve,
                                    setUpdates,
                                    setAction,
                                    setInstruction,
                                    setClearPoNumber,
                                }}
                            />

                            <HR />
                        </>
                    );
                }
                break;
            }
        }
    }

    return (
        <ResponsiveSidebarDialog
            open={!!exception}
            onClose={callbacks.onClose}
            fullWidth={true}
            maxWidth="md"
            css={css`
                .MuiDialog-container {
                    align-items: normal;
                }
            `}
        >
            <ModalTitle
                border
                title={`Order ${order.order_number || ''}${order.po_number ? ` / ${order.po_number}` : ''} - ${
                    order.order_type
                }`}
                onClose={callbacks.onClose}
            />
            <DialogContent
                css={css`
                    width: 100%;
                    padding: 0;
                `}
            >
                <Grid
                    constainer
                    direction="column"
                    css={css`
                        height: 100%;
                        padding: 0 24px;

                        padding-top: 32px;
                    `}
                >
                    {resolutions}

                    {exception.type === 'shortage' && exception.status === 'OPEN' && (
                        <Grid
                            container
                            direction="row"
                            css={css`
                                margin-bottom: 20px;
                            `}
                        >
                            <PrimaryButton
                                onClick={() => {
                                    callbacks.onResolve({
                                        exception: {
                                            exception_id: exception.exception_id,
                                            route_id: exception.route_id,
                                            status: 'RESOLVED',
                                            resolution: ACTIONS.RESOLVE_NOOP,
                                            instruction: INSTRUCTIONS.NOOP,
                                            type: exception.type,
                                        },
                                    });
                                }}
                            >
                                Items Found
                            </PrimaryButton>
                            <Tooltip
                                style={{ color: '#59B863' }}
                                title={
                                    'If the items associated with this exception were reported short in error and have since been found, click this button to cancel the exception.'
                                }
                                placement="top"
                            >
                                <IconButton size="small">
                                    <InfoOutlined />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    )}

                    <OrderInfo order={order} exception={exception} />

                    {exception.type === 'generalIntegrationException' && (
                        <Grid
                            direction="row"
                            container
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            <Grid
                                direction="column"
                                container
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                    color: red;
                                `}
                            >
                                <Body1>
                                    *This is a general exception reported by an integrated TMS. The carrier needs to
                                    update it with the proper specific incident information.
                                </Body1>
                            </Grid>
                        </Grid>
                    )}

                    {items ? <ItemInfo items={items} /> : null}

                    <ExceptionInfo exception={exception} />

                    {isOnwardAdmin && (
                        <>
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    margin-bottom: 8px;
                                `}
                            >
                                <Body2>Fee Approval (Only visible to Onward Admins)</Body2>
                            </Grid>
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    margin-bottom: 24px;
                                `}
                            >
                                <Body1>
                                    By default, attempt fees are automatically added to the order's accessorials when an
                                    exception is reported. If the reason for the exception was controllable by the
                                    carrier or if fees should otherwise not be applied, you can modify the{' '}
                                    <Link
                                        to={`/accounting/accessorials/${order.order_id}/shipper`}
                                        style={{ color: '#59B863' }}
                                        target="_blank"
                                    >
                                        shipper accessorials here
                                    </Link>
                                    , and modify{' '}
                                    <Link
                                        to={`/accounting/accessorials/${order.order_id}/carrier`}
                                        style={{ color: '#59B863' }}
                                        target="_blank"
                                    >
                                        carrier accessorials here
                                    </Link>
                                </Body1>
                            </Grid>
                        </>
                    )}
                </Grid>
                {resolveError && (
                    <Alert
                        severity="error"
                        css={css`
                            margin-top: 16px;
                        `}
                    >
                        {resolveError}
                    </Alert>
                )}
            </DialogContent>
            <StickyModalActions border="true">
                <Grid>
                    <SecondaryButton onClick={callbacks.onClose}>Close</SecondaryButton>
                </Grid>
                {canResolve && (
                    <Grid>
                        <PrimaryButton
                            loading={inFlight || loading}
                            disabled={!isValid}
                            onClick={() => {
                                setResolveError(null);
                                Promise.resolve(resolve()).catch((err) => {
                                    captureException(err, err?.sentryExtra ? { extra: err.sentryExtra } : undefined);
                                    setResolveError(err?.message || 'Something went wrong resolving this exception.');
                                    setInflight(false);
                                });
                            }}
                        >
                            Resolve
                        </PrimaryButton>
                    </Grid>
                )}
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default ExceptionResolutionModal;
